import { AfterViewInit, Component, inject, Input, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { InputTextareaModule } from "primeng/inputtextarea";
import { InputTextModule } from "primeng/inputtext";
import { ButtonDirective } from "primeng/button";
import { BehaviorSubject, firstValueFrom, forkJoin } from "rxjs";
import { DividerModule } from "primeng/divider";
import {
  AccessRole,
  ButtonColumnType,
  CustomTableComponent,
  CustomTitle, DataColumnType,
  FormGroupErrorMessageComponent, ICustomTable, ISettings,
  LoadingPanelService, LocalService,
  SafeResourcePipe
} from "lib-base";
import Quill from 'quill';

import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import { CorreoTemplateService } from "../../services/database/correo-template.service";
import { CorreoPlantillaDto, ICorreoPlantillaEditDto } from "../../shared/interfaces/database/configuracion";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { DialogModule } from "primeng/dialog";
import { TableLazyLoadEvent, TableModule, TableRowSelectEvent } from 'primeng/table';
import { Clipboard } from '@angular/cdk/clipboard';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { ConfirmationService, MenuItem, MenuItemCommandEvent, Message, MessageService } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { TabViewModule } from "primeng/tabview";
import { CleanTemplate } from "../../shared/clases/cleanTemplate";
import { MessagesModule } from "primeng/messages";
import { Event } from "@angular/router";
import {CustomTitleDirective} from "../../shared/clases/custom-title.directive";


@Component({
  selector: 'app-correo-template',
  standalone: true,
  imports: [QuillModule, FormsModule, ReactiveFormsModule, InputTextareaModule,
    InputTextModule, ButtonDirective, DividerModule, SafeResourcePipe,
    FormGroupErrorMessageComponent, DialogModule, TableModule,
    ContextMenuModule, ToastModule, CustomTitle,
    TranslatePipe, CustomTableComponent, TabViewModule, MessagesModule],
  templateUrl: './correo-template.component.html',
  styleUrl: './correo-template.component.scss'
})
export class CorreoTemplateComponent implements OnInit, AfterViewInit {

  @Input() isEditable: boolean = false;
  variables: string[] = [];
  changesVariable$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  showTemplate: boolean = false;
  form!: FormGroup;
  templateHtml: string = '';
  translations: any;
  quillConfig: any;
  variableSelected: any;
  variable: string | undefined;
  modelContextMenu: MenuItem[] = [];
  private id: number | undefined;

  messages: Message[] = [];
  role = AccessRole;
  customTable !: ICustomTable;

  activeTabIndex: number = 0;
  title: WritableSignal<string> = signal<string>('');
  @ViewChild('editorHtml') editor: QuillEditorComponent | undefined;
  @ViewChild(ContextMenu, { static: true }) cm: ContextMenu | undefined;
  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;
  private titleService:CustomTitleDirective = inject(CustomTitleDirective);

  constructor(private correoTemplateService: CorreoTemplateService,
    private readonly _lps: LoadingPanelService, protected readonly ls: LocalService,
    private clipboard: Clipboard, private messageService: MessageService,
    private readonly _cs: ConfirmationService, private _t: TranslateService) {
    this.initForm();
    this.initQuillConfig();
  }

  ngOnInit(): void {
        this.titleService.changeTitle('components.app-correo-plantilla.title');
    this.initTranslations()
      .then(() => this.getConfigTable());
    this.initModelContextMenu();
  }

  ngAfterViewInit(): void {
    if (!this.cm) return;
    this.changesVariable$.asObservable().subscribe({
      next: value => {
        const menuItem = this.cm?._model?.find((it: MenuItem) => it.id == 'variable');
        if (menuItem) {
          menuItem.items = value?.map(item => {
            return {
              label: item,
              command: ($event: MenuItemCommandEvent) => this.commandPaste($event)
            }
          }) as MenuItem[] ?? [];
          if (this.cm?.model)
            this.cm.model = [menuItem];
        }
      }
    });

  }



  private initForm() {
    this.form = new FormGroup({
      nombre: new FormControl(null, [Validators.required]),
      description: new FormControl(null, [Validators.required]),
      template: new FormControl(null, [Validators.required]),
    });
    this.form.get('template')?.valueChanges.subscribe((value) => {
      this.templateHtml = value;
    })

  }

  private initQuillConfig() {
    this.quillConfig = {
      toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        [{ font: [] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['link', 'image'],
        [{ color: [] }, { background: [] }],
        [{ list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ align: [] }],
        ['clean']
      ],
      table: false,

    };
  }

  private initModelContextMenu(): void {
    this.modelContextMenu = [
      {
        id: 'variable',
        label: 'Variables', icon: 'pi pi-variable',
        items: []
      }
    ]
  }

  private initMessage(): void {
    this.messages = [
      {
        severity: 'info',
        summary: this.translations.msgInfo
      },
    ];
  }

  private async initTranslations() {
    this.translations = await firstValueFrom(forkJoin({
      title: this._t.get('components.app-correo-plantilla.title'),
      titleActualizar: this._t.get('components.app-correo-plantilla.title-actualizar'),
      subject: this._t.get('components.app-correo-plantilla.subject'),
      name: this._t.get('components.app-correo-plantilla.name'),
      description: this._t.get('components.app-correo-plantilla.description'),
      content: this._t.get('components.app-correo-plantilla.content'),
      placeholderContent: this._t.get('components.app-correo-plantilla.placeholder_content'),
      preview: this._t.get('components.app-correo-plantilla.preview'),
      headerVariable: this._t.get('components.app-correo-plantilla.header-variables'),
      variableSeleccionada: this._t.get('components.app-correo-plantilla.variable-seleccionada'),
      placeholderAgregar: this._t.get('components.app-correo-plantilla.placeholder-agregar'),
      id: this._t.get('components.app-correo-plantilla.id'),
      nombrePLantilla: this._t.get('components.app-correo-plantilla.nombre-plantilla'),
      descripcionPlantilla: this._t.get('components.app-correo-plantilla.descripcion-plantilla'),
      estado: this._t.get('components.app-correo-plantilla.estado'),
      msgSuccess: this._t.get('common.success'),
      summaryWarn: this._t.get('form.msg.warn-summary'),
      completeCampos: this._t.get('form.msg.complete-campos'),
      requiereRolSeguridad: this._t.get('msg.requiere-rol-seguridad'),
      estaSeguroEliminar: this._t.get('msg.confirmarEliminarFila'),
      correoActivado: this._t.get('components.app-correo-plantilla.correo-activado'),
      correoEliminado: this._t.get('components.app-correo-plantilla.correo-eliminado'),
      correoCreado: this._t.get('components.app-correo-plantilla.correo-creado'),
      correoActualizado: this._t.get('components.app-correo-plantilla.correo-actualizado'),
      msgWarning: this._t.get('components.app-correo-plantilla.advertencia-solicitud'),
      msgInfo: this._t.get('components.app-correo-plantilla.msg-info'),
      msgWarnTemplate: this._t.get('components.app-correo-plantilla.warn-template'),
      version: this._t.get('common.version'),
    }));
  }

  private getConfigTable() {
    this.customTable = {
      columns: [
        {
          field: 'id',
          header: this.translations.id,
          class: 'font-13',
          width: '22em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          dataType: DataColumnType.NUMBER
        },
        {
          field: 'version',
          header: this.translations.version,
          class: 'font-13',
          width: '1rem',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
          },
          dataType: DataColumnType.NUMBER
        },
        {
          field: 'nombre',
          header: this.translations.nombrePLantilla,
          class: 'font-13',
          width: '22em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'descripcion',
          header: this.translations.descripcionPlantilla,
          class: 'font-13',
          width: '22em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          dataType: DataColumnType.TEXT
        }, {
          field: 'estado',
          header: this.translations.estado,
          class: 'button-edit p-button-outlined',
          width: '6rem',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          buttonType: ButtonColumnType.CHECK,
          onClick: (event: Event, data: ICorreoPlantillaEditDto) =>
            this.desactivarTemplate(event, data),
        },
        {
          field: 'edit',
          header: '',
          width: '8rem',
          class: 'pt-2 pb-2 pr-1',
          buttonType: ButtonColumnType.EDIT,
          disabled: !this.ls.hasRole(this.role.ADMIN_SEC),
          tooltip: {
            message: this.translations.requiereRolSeguridad
          },
          onClick: (event: Event, data: ICorreoPlantillaEditDto) =>
            this.editModal(data),
          headerBtn: {
            icon: 'pi pi-plus',
            class: 'pt-2 pb-2 pl-0 pr-0',
            onClick: () => this.openNewModal(),
          }
        }
      ],
      settings: {
        table: {
          rows: 8,
          lazy: true
        }
      } as ISettings
    } as ICustomTable;
  }

  lazyloadRecords(event: TableLazyLoadEvent) {
    this.getAllTemplates(event);
  }
  //request
  private getAllTemplates(event: TableLazyLoadEvent) {

    this.correoTemplateService.getAllTemplates(event).subscribe({
      next: value => {
        if (this.customTableComponent) {
          this.customTableComponent.data = value.content;
          this.customTableComponent.totalRecords = value.totalElements;
        }
      }
    })
  }

  createOrUpdateTemplate() {
    if (this.validateText()) return;
    this._lps.show();
    const body = this.build();
    if (!body) return;
    if (!this.id) {
      this.correoTemplateService.createPlantilla(body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translations.success,
            detail: this.translations.correoCreado
          });
          this._lps.hide();
          this.closeModal();
          this.getAllTemplates({});
        },
        error: () => {
          this._lps.hide();
          this.messageService.add({
            severity: 'warn',
            summary: this.translations.summaryWarn,
            detail: this.translations.msgWarning
          });
        }
      })
    } else {
      this.actualizarTemplateRequest(body)
    }
  }

  private actualizarTemplateRequest(body: CorreoPlantillaDto) {
    if (!this.id) return;
    this.correoTemplateService.actualizarPlantilla(this.id, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translations.success,
          detail: this.translations.correoActualizado
        });
        this._lps.hide();
        this.closeModal();
        this.getAllTemplates({});
      }, error: () => {
        this._lps.hide();
        this.messageService.add({
          severity: 'warn',
          summary: this.translations.summaryWarn,
          detail: this.translations.msgWarning
        });
      }
    })

  }

  desactivarTemplate(event: any, data: ICorreoPlantillaEditDto) {
    if (data.estado) {
      this._cs.confirm({
        target: event.target as EventTarget,
        message: this.translations.estaSeguroEliminar,
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.desactivar(data.id, this.translations.correoEliminado)
      });
    } else {
      this.desactivar(data.id, this.translations.correoActivado);
    }

  }

  private desactivar(id: number, message: string): void {
    this._lps.show(true);
    this.correoTemplateService.desactivar(id).subscribe({
      next: () => {
        this._lps.hide();
        this.messageService.add({
          severity: 'success',
          summary: this.translations.success,
          detail: message
        });
        this.getAllTemplates({});
      }
    });
  }


  editModal(data: ICorreoPlantillaEditDto): void {
    this.openNewModal(this.translations.titleActualizar);
    this.showTemplate = true;
    this.form.reset();
    this.id = data.id;
    this.form.setValue({
      nombre: data.nombre,
      description: data.descripcion,
      template: data.contenido,
    });
    this.variables = Array.from(new Set<string>(data.variables));
    this.changesVariable$.next(this.variables);

  }

  openNewModal(title: string = this.translations.title): void {
    this.title.set(title);
    this.showTemplate = true;
    this.initMessage();
  }

  closeModal() {
    this.showTemplate = false;
    this.form.reset();
    this.activeTabIndex = 0;
    this.id = undefined;
    this.templateHtml = '';
  }

  private build(): CorreoPlantillaDto | null {
    if (!this.editor) return null;
    const quill = this.editor?.quillEditor;
    const variables = this.variables ?? [];
    CleanTemplate.template(quill, variables);
    return {
      nombre: this.form.get('nombre')?.value,
      descripcion: this.form.get('description')?.value,
      contenido: quill.getSemanticHTML(),
      variables: variables
    }
  }

  selectionItem(item: TableRowSelectEvent) {
    this.clipboard.copy('${' + item.data + '}');
    this.messageService.add({
      key: 'confirm', severity: 'success',
      summary: this.translations?.msgSuccess,
      detail: this.translations?.variableSeleccionada,
      closable: false
    });
  }

  addVariable() {
    if (!this.variable) return
    if (!this.variables.includes(this.variable)) {
      this.variables.push(this.variable);
      this.changesVariable$.next(this.variables);
      this.variable = undefined;
    }
  }

  deleteVariable = (idx: number) => {
    this.variables.splice(idx, 1);
    this.changesVariable$.next(this.variables);
  }

  onContextMenu($event: any) {
    this.cm?.show($event);
  }

  commandPaste($event: any) {
    if (this.editor) {
      const quill = this.editor.quillEditor;
      const selection = quill.getSelection(true);
      const text = '${' + ($event.item.label as string)?.trim() + '}';

      let index = selection ? selection.index : 0;
      let length = selection ? selection.length : 0;
      if (length > 0) quill.deleteText(index, length, Quill.sources.SILENT);
      quill.insertText(index, text, Quill.sources.USER);
      quill.setSelection(index + text.length, 0, Quill.sources.SILENT);
      quill.focus();
    }
  }

  onEditorCreated($event: any) {
    CleanTemplate.onEditorCreated($event);
  }

  onChangeContent(value: string | null) {
    if (value)
      this.templateHtml = value;
  }

  private validateText(): boolean {
    const text = this.form.get('template')?.value;

    const value = CleanTemplate.checkBraceBalance(text)
    if (!value.mismatched) return false;
    this.messageService.add({
      severity: 'warn',
      summary: this.translations.summaryWarn,
      detail: this.translations?.msgWarnTemplate,
    });
    if (this.editor) {
      const quill = this.editor.quillEditor;
      quill.setSelection(value.position || 0, 0, Quill.sources.SILENT);
    }
    return true;
  }


  public blockSpace(event: KeyboardEvent): void {
    const regex = /^([a-zA-Z0-9_]+)$/;
    if (!regex.test(event.key))
      event.preventDefault();
  }
}

